import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { useI18n } from '../i18n';
import { EmptyState, ErrorState, Skeleton } from '../components';
import { isDesktop } from '../components/DesktopBar';
import { formatMinor } from '../money';

/**
 * What the rules decided while a computer worked offline (ADR-0009).
 *
 * Not a conflict screen: nothing here waits for a person to choose between
 * two values. The record already stands as the rule left it — the newest
 * field, the booking kept in red, the duplicate line dropped, the payment
 * spread, the credit held — and this list says so, in sentences, so the desk
 * can glance, act where it wants to (move the booking, reverse the double
 * payment), and acknowledge. On the Windows application a second section
 * shows what THIS computer sent and what became of each change, which also
 * works with no server in reach.
 */
interface Notice {
  id: string;
  kind: string;
  severity: 'info' | 'attention';
  entity: string | null;
  entityId: string | null;
  patient: { id: string; patientNumber: string; givenName: string; familyName: string; givenNameAlt: string | null; familyNameAlt: string | null } | null;
  by: string | null;
  device: string | null;
  detail: Record<string, unknown>;
  occurredAt: string;
  createdAt: string;
  acknowledgedAt: string | null;
}

interface LogEntry {
  operationId: string;
  entity: string;
  entityId: string;
  action: string;
  state: string;
  occurredAt: string;
  acknowledgedAt: string | null;
  note: string | null;
  flags: string[];
  lastError: string | null;
  payload: Record<string, unknown>;
}

export function Sync({ currency, onGo, onOpenPatient }: { currency: string; onGo: (page: string) => void; onOpenPatient?: (patientId: string) => void }) {
  const { t, tx, locale, formatDate, formatTime, personName } = useI18n();
  const [notices, setNotices] = useState<Notice[] | null>(null);
  const [cached, setCached] = useState(false);
  const [all, setAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [log, setLog] = useState<{ log: LogEntry[]; clockOffsetMs: number } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const s = t.sync;

  const load = useCallback(async () => {
    try {
      const r = (await api.syncNotices(all)) as { notices: Notice[]; cached?: boolean };
      setNotices(r.notices);
      setCached(r.cached === true);
      setError(null);
    } catch {
      setError(t.common.error);
    }
    if (isDesktop()) {
      try {
        const r = await fetch('/api/local/sync/log');
        if (r.ok) setLog((await r.json()) as { log: LogEntry[]; clockOffsetMs: number });
      } catch {
        /* no log */
      }
    }
  }, [all, t]);

  useEffect(() => {
    void load();
  }, [load, nonce]);

  async function ack(id: string) {
    setBusy(id);
    try {
      await api.ackNotice(id);
      setNotices((list) => (list ? list.map((n) => (n.id === id ? { ...n, acknowledgedAt: new Date().toISOString() } : n)) : list));
    } catch {
      setError(t.common.error);
    } finally {
      setBusy(null);
    }
  }

  const money = (minor: unknown, cur?: unknown) => formatMinor(Number(minor ?? 0), typeof cur === 'string' && cur ? cur : currency, locale);

  /** The sentence for a notice, from its kind and detail. */
  function sentence(n: Notice): string {
    const d = n.detail;
    const tpl = (s.detail as Record<string, string>)[n.kind] ?? '';
    switch (n.kind) {
      case 'booking_overlap':
        return tx(tpl, { at: d.startsAt ? `${formatDate(String(d.startsAt))} ${formatTime(String(d.startsAt))}` : '—' });
      case 'possible_duplicate_payment':
        return tx(tpl, { amount: money(d.amountMinor, d.currency) });
      case 'payment_applied': {
        const applied = Array.isArray(d.allocations) ? (d.allocations as { amountMinor: number }[]).reduce((a, x) => a + Number(x.amountMinor ?? 0), 0) : 0;
        const credit = Number(d.creditMinor ?? 0);
        return tx(tpl, { amount: money(d.amountMinor, d.currency), applied: money(applied, d.currency), credit: credit > 0 ? tx(s.detail.payment_credit, { credit: money(credit, d.currency) }) : '' });
      }
      case 'invoice_numbered':
        return tx(tpl, { ref: String(d.provisionalRef ?? '—'), number: String(d.number ?? '—') });
      case 'line_dropped':
        return tx(tpl, { description: String(d.description ?? '') });
      case 'invoice_dropped':
        return tx(tpl, { ref: String(d.provisionalRef ?? '—') });
      case 'possible_duplicate_treatment':
        return tx(tpl, { name: String(d.name ?? ''), tooth: d.toothFdi ? `${t.chart.tooth} ${String(d.toothFdi)}` : d.zone ? ((t.zones.zone as Record<string, string>)[String(d.zone)] ?? String(d.zone)) : '—' });
      case 'stock_negative':
        return tx(tpl, { name: String(locale === 'ar' ? d.nameAr ?? d.nameEn : d.nameEn ?? d.nameAr ?? ''), onHand: String(d.onHand ?? 0) });
      case 'superseded_edit':
        return tx(tpl, { fields: Array.isArray(d.fields) ? (d.fields as string[]).join(', ') : '' });
      case 'superseded_status':
        return tx(tpl, { kept: labState(String(d.kept ?? '')), wanted: labState(String(d.wanted ?? '')) });
      case 'not_applied':
        return tx(tpl, { message: String(d.message ?? '') });
      default:
        return tpl;
    }
  }

  function labState(state: string): string {
    return (t.lab.states as Record<string, string>)[state] ?? state.replace(/_/g, ' ');
  }

  function goFor(n: Notice): { label: string; page: string } | null {
    if (n.kind === 'booking_overlap' || n.kind === 'duplicate_booking') return { label: s.openCalendar, page: 'appointments' };
    if (['possible_duplicate_payment', 'payment_applied', 'invoice_numbered', 'line_dropped', 'invoice_dropped'].includes(n.kind)) return { label: s.openFinance, page: 'finance' };
    if (n.kind === 'stock_negative') return { label: s.openInventory, page: 'inventory' };
    return null;
  }

  const open = (notices ?? []).filter((n) => !n.acknowledgedAt);
  const attention = open.filter((n) => n.severity === 'attention');
  const info = open.filter((n) => n.severity === 'info');
  const done = (notices ?? []).filter((n) => n.acknowledgedAt);

  function row(n: Notice) {
    const go = goFor(n);
    return (
      <div key={n.id} className={`sync-notice ${n.severity === 'attention' && !n.acknowledgedAt ? 'is-attention' : ''}`}>
        <div className="sync-notice-main">
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <strong>{(s.kinds as Record<string, string>)[n.kind] ?? n.kind}</strong>
            {n.patient ? (
              onOpenPatient ? (
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => onOpenPatient(n.patient!.id)}>
                  {personName(n.patient)} <span className="ltr-value">{n.patient.patientNumber}</span>
                </button>
              ) : (
                <span>
                  {personName(n.patient)} <span className="ltr-value">{n.patient.patientNumber}</span>
                </span>
              )
            ) : null}
          </div>
          <p style={{ margin: '4px 0 0' }}>{sentence(n)}</p>
          <small style={{ color: 'var(--ink-3)' }}>
            {formatDate(n.occurredAt)} {formatTime(n.occurredAt)}
            {n.by ? ` · ${tx(s.by, { name: n.by })}` : ''}
            {n.device ? ` · ${tx(s.fromDevice, { device: n.device })}` : ''}
            {n.acknowledgedAt ? ` · ${s.acknowledged}` : ''}
          </small>
        </div>
        <div className="row" style={{ gap: 6, flexShrink: 0 }}>
          {go ? (
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => onGo(go.page)}>
              {go.label}
            </button>
          ) : null}
          {!n.acknowledgedAt ? (
            <button type="button" className="btn btn-sm" disabled={busy === n.id} onClick={() => void ack(n.id)}>
              {s.acknowledge}
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1>{s.title}</h1>
          <p className="page-sub">{s.sub}</p>
        </div>
        <label className="row" style={{ gap: 8, alignItems: 'center' }}>
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          <span>{s.showAll}</span>
        </label>
      </div>

      {cached ? (
        <div className="notice">
          <span>{t.desktop.offlineCached}</span>
        </div>
      ) : null}
      {error ? <ErrorState message={error} onRetry={() => setNonce((n) => n + 1)} /> : null}

      <div className="card">
        <div className="card-head">
          <h2>
            {s.attention}
            {attention.length > 0 ? <span className="count-chip warn">{attention.length}</span> : null}
          </h2>
        </div>
        {notices === null ? <Skeleton rows={2} /> : attention.length === 0 ? <EmptyState title={s.nothingOpen} /> : <div className="card-body sync-list">{attention.map(row)}</div>}
      </div>

      <div className="card">
        <div className="card-head">
          <h2>
            {s.forTheRecord}
            {info.length > 0 ? <span className="count-chip">{info.length}</span> : null}
          </h2>
        </div>
        {notices === null ? <Skeleton rows={2} /> : info.length === 0 && done.length === 0 ? <EmptyState title={s.nothingYet} /> : <div className="card-body sync-list">{[...info, ...done].map(row)}</div>}
      </div>

      {log ? (
        <div className="card">
          <div className="card-head">
            <h2>{s.sentFromHere}</h2>
          </div>
          <div className="card-body stack">
            <p className="page-sub" style={{ margin: 0 }}>{s.sentFromHereSub}</p>
            {Math.abs(log.clockOffsetMs) >= 60_000 ? (
              <div className="notice warn">
                <span>{tx(t.desktop.clockOffset, { n: Math.round(Math.abs(log.clockOffsetMs) / 60_000) })}</span>
              </div>
            ) : null}
            {log.log.length === 0 ? (
              <EmptyState title={s.noLog} />
            ) : (
              <table className="table">
                <thead>
                  <tr>
                    <th>{t.common.status}</th>
                    <th />
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {log.log.map((e) => (
                    <tr key={e.operationId}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <span className={`badge ${e.state === 'recovery' ? 'danger' : e.state === 'acknowledged' ? (e.flags.length > 0 ? 'warn' : 'ok') : ''}`}>
                          {(s.state as Record<string, string>)[e.state] ?? e.state}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(e.occurredAt)} {formatTime(e.occurredAt)}
                      </td>
                      <td>
                        <strong>{(s.entities as Record<string, string>)[e.entity] ?? e.entity}</strong> {(s.actions as Record<string, string>)[e.action] ?? e.action}
                        {e.flags.length > 0 ? (
                          <span style={{ marginInlineStart: 8 }}>
                            {e.flags.map((f) => (
                              <span key={f} className="badge warn" style={{ marginInlineEnd: 4 }}>
                                {(s.flags as Record<string, string>)[f] ?? f}
                              </span>
                            ))}
                          </span>
                        ) : null}
                        {e.note ? <div style={{ color: 'var(--ink-2)', fontSize: 12.5 }}>{e.note}</div> : null}
                        {e.lastError && e.state === 'recovery' ? <div style={{ color: 'var(--danger)', fontSize: 12.5 }}>{e.lastError}</div> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card-head">
          <h2>{s.rules}</h2>
        </div>
        <div className="card-body">
          <ul style={{ margin: 0, paddingInlineStart: 20, color: 'var(--ink-2)' }}>
            {s.rulesText.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
